import React, { useState } from 'react';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import split from 'lodash/split';

import styles from '../ProductDetail.module.css';

const DISCLAIMER_COPY = {
  dropship:
    'This is a Drop Ship item that will ship directly from the retailer, separately from the rest of your order.',
  pao: 'For products with an expiration date, the expiration date will fall at least 6 months after the products are shipped from the FabFitFun warehouse. Note that many products do not contain an expiration date, but do contain a Period After Opening (“PAO”) date, which tells you how long a product will remain effective after it has been opened.',
  cbd: 'Information on this site is for reference purposes only. Product manufacturers provide all product information. FabFitFun does not represent or warrant that this information is accurate or complete. We recommend that you contact the manufacturer directly if you have specific product concerns or questions.',
};
const MEMBERSHIP_INFO = 'Membership Info';
const ADDITIONAL_INFO = 'Additional Info';

const formatList = (string) => split(string, ',');

const ProductDescription = ({
  layout,
  shortDescription,
  includes,
  isParisHiltonKit,
  features,
  color,
  design,
  size,
  dimensions,
  howToUse,
  ingredients,
  materials,
  techCompatibility,
  careInstructions,
  pao,
  additionalInfo,
  countryOfOrigin,
  dropshipDisclaimer,
  paoDisclaimer,
  cbdDisclaimer,
  description,
  shortDescriptionFieldsFF,
  loggedIn,
}) => {
  const includesList = formatList(includes);
  const additionalInfoList = formatList(additionalInfo);
  // layout "medium" = PDP | layout "small" = product accordion
  const showShortDescriptionFields =
    layout === 'medium' || (layout === 'small' && shortDescriptionFieldsFF);
  const [isIncludesOpen, setIsIncludesOpen] = useState(true);
  const icon = isIncludesOpen ? 'fal fa-minus' : 'fal fa-plus';
  const showAdditionalInfo =
    additionalInfo && (!isParisHiltonKit || (isParisHiltonKit && !loggedIn));
  const additionalInfoTitle = isParisHiltonKit
    ? MEMBERSHIP_INFO
    : ADDITIONAL_INFO;

  const toggleIsOpen = () => setIsIncludesOpen((prevState) => !prevState);

  return (
    <>
      {showShortDescriptionFields ? (
        <>
          {shortDescription && (
            <div
              className={styles.shortDescription}
              dangerouslySetInnerHTML={{ __html: shortDescription }}
            />
          )}
          {includes && (
            <>
              <div className={styles.includesWrapper}>
                <h3>Includes</h3>
                {isParisHiltonKit && (
                  <button
                    type="button"
                    className="naked-button"
                    onClick={toggleIsOpen}
                  >
                    <i className={icon} />
                  </button>
                )}
              </div>
              {isIncludesOpen && (
                <ul>
                  {map(includesList, (item, index) => {
                    if (!item) return null;
                    return <li key={index}>{item}</li>;
                  })}
                </ul>
              )}
            </>
          )}
          {features && (
            <>
              <h3>Features</h3>
              <p>{features}</p>
            </>
          )}
          {color && (
            <>
              <h3>Color</h3>
              <p>{color}</p>
            </>
          )}
          {design && (
            <>
              <h3>Design</h3>
              <p>{design}</p>
            </>
          )}
          {size && (
            <>
              <h3>Size</h3>
              <p>{size}</p>
            </>
          )}
          {dimensions && (
            <>
              <h3>Dimensions</h3>
              <p>{dimensions}</p>
            </>
          )}
          {howToUse && (
            <>
              <h3>How to use</h3>
              <p>{howToUse}</p>
            </>
          )}
          {ingredients && (
            <>
              <h3>Ingredients</h3>
              <p>{ingredients}</p>
            </>
          )}
          {materials && (
            <>
              <h3>Materials</h3>
              <p>{materials}</p>
            </>
          )}
          {techCompatibility && (
            <>
              <h3>Tech Compatibility</h3>
              <p>{techCompatibility}</p>
            </>
          )}
          {careInstructions && (
            <>
              <h3>Care Instructions</h3>
              <p>{careInstructions}</p>
            </>
          )}
          {pao && (
            <>
              <h3>PAO</h3>
              <p>{pao}</p>
            </>
          )}
          {showAdditionalInfo && (
            <div className={styles.additionalInfo}>
              <h3>{additionalInfoTitle}</h3>
              <ul>
                {map(additionalInfoList, (item, index) => {
                  if (!item) return null;
                  return <li key={index}>{item}</li>;
                })}
              </ul>
            </div>
          )}
          {countryOfOrigin && (
            <>
              <h3>Country of Origin</h3>
              <p>{countryOfOrigin}</p>
            </>
          )}
          {(dropshipDisclaimer || paoDisclaimer || cbdDisclaimer) && (
            <div className={styles.disclaimersContainer}>
              {dropshipDisclaimer && (
                <p className={styles.disclaimer}>{DISCLAIMER_COPY.dropship}</p>
              )}
              {paoDisclaimer && (
                <p className={styles.disclaimer}>{DISCLAIMER_COPY.pao}</p>
              )}
              {cbdDisclaimer && (
                <p className={styles.disclaimer}>{DISCLAIMER_COPY.cbd}</p>
              )}
            </div>
          )}
        </>
      ) : (
        <div
          className={styles.description}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
    </>
  );
};

ProductDescription.propTypes = {
  layout: PropTypes.string,
  shortDescription: PropTypes.string,
  includes: PropTypes.string,
  isParisHiltonKit: PropTypes.bool,
  features: PropTypes.string,
  color: PropTypes.string,
  design: PropTypes.string,
  size: PropTypes.string,
  dimensions: PropTypes.string,
  howToUse: PropTypes.string,
  ingredients: PropTypes.string,
  materials: PropTypes.string,
  techCompatibility: PropTypes.string,
  careInstructions: PropTypes.string,
  pao: PropTypes.string,
  additionalInfo: PropTypes.string,
  countryOfOrigin: PropTypes.string,
  dropshipDisclaimer: PropTypes.bool.isRequired,
  paoDisclaimer: PropTypes.bool.isRequired,
  cbdDisclaimer: PropTypes.bool.isRequired,
  description: PropTypes.string,
  shortDescriptionFieldsFF: PropTypes.bool,
  loggedIn: PropTypes.bool.isRequired,
};

ProductDescription.defaultProps = {
  layout: 'medium',
  shortDescription: null,
  includes: null,
  isParisHiltonKit: false,
  features: null,
  color: null,
  design: null,
  size: null,
  dimensions: null,
  howToUse: null,
  ingredients: null,
  materials: null,
  techCompatibility: null,
  careInstructions: null,
  pao: null,
  additionalInfo: null,
  countryOfOrigin: null,
  description: null,
  shortDescriptionFieldsFF: true,
};

export default ProductDescription;
